import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, AlertCircle } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [showPwd, setShowPwd] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      const to = location.state?.from?.pathname || "/app";
      navigate(to, { replace: true });
    } catch (err) {
      setError(err.message || "Impossible de se connecter");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="page-login"
      className="min-h-screen flex flex-col relative overflow-hidden bg-[#F7F8FA]"
    >
      {/* Halo décoratif */}
      <div
        aria-hidden
        className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#C8FF2F]/25 blur-3xl"
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -left-40 h-[420px] w-[420px] rounded-full bg-[#D9FFE8]/40 blur-3xl"
      />

      {/* Header logo */}
      <header className="relative z-10 px-6 py-8">
        <div className="max-w-md mx-auto">
          <Logo variant="full" size="md" rounded="rounded-lg" />
        </div>
      </header>

      {/* Formulaire centré */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md">
          <div className="wyvio-fade-in">
            <h1
              data-testid="login-title"
              className="text-[36px] sm:text-[42px] leading-[1.05] font-semibold tracking-tight text-[#111111]"
            >
              Bienvenue
            </h1>
            <p className="mt-3 text-[15px] text-[#6B7280] leading-relaxed">
              Connectez-vous à votre espace wyvio pour suivre vos dossiers.
            </p>

            <form
              onSubmit={handleSubmit}
              className="mt-10 space-y-4"
              data-testid="login-form"
            >
              <div>
                <label className="text-[12px] font-medium text-[#6B7280]">
                  Adresse e-mail
                </label>
                <input
                  data-testid="login-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="stephane@prestige-auto.fr"
                  className="mt-1.5 w-full h-12 rounded-2xl bg-white border border-[#EEF2F5] px-4 text-[14px] text-[#111111] placeholder:text-[#9CA3AF] outline-none focus:border-[#111111] transition-colors"
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-[12px] font-medium text-[#6B7280]">
                    Mot de passe
                  </label>
                  <Link
                    to="/forgot-password"
                    data-testid="login-forgot"
                    className="text-[12px] font-medium text-[#111111] hover:opacity-70 transition-opacity"
                  >
                    Oublié ?
                  </Link>
                </div>
                <div className="mt-1.5 relative">
                  <input
                    data-testid="login-password"
                    type={showPwd ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full h-12 rounded-2xl bg-white border border-[#EEF2F5] px-4 pr-11 text-[14px] text-[#111111] placeholder:text-[#9CA3AF] outline-none focus:border-[#111111] transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    data-testid="login-toggle-password"
                    className="absolute inset-y-0 right-0 w-11 flex items-center justify-center text-[#9CA3AF] hover:text-[#111111]"
                    aria-label="Afficher/masquer le mot de passe"
                  >
                    {showPwd ? <EyeOff size={16} strokeWidth={1.75} /> : <Eye size={16} strokeWidth={1.75} />}
                  </button>
                </div>
              </div>

              <button
                data-testid="login-submit"
                type="submit"
                disabled={loading}
                className="mt-6 w-full h-12 rounded-full bg-[#111111] text-white text-[14px] font-semibold inline-flex items-center justify-center gap-2 hover:bg-black active:scale-[0.99] transition-all shadow-[0_10px_30px_-10px_rgba(17,17,17,0.4)] disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? "Connexion…" : "Se connecter"}
                {!loading && <ArrowRight size={15} strokeWidth={2.25} />}
              </button>

              {error && (
                <div
                  data-testid="login-error"
                  className="mt-3 flex items-start gap-2 rounded-xl bg-[#FEE2E2] px-3.5 py-2.5 text-[12.5px] text-[#991B1B]"
                >
                  <AlertCircle size={13} strokeWidth={2} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </form>

            <p className="mt-8 text-center text-[12px] text-[#6B7280]">
              Pas encore d&apos;agence sur wyvio ?{" "}
              <Link
                to="/contact?type=demo"
                data-testid="login-request-demo"
                className="font-medium text-[#111111] hover:opacity-70"
              >
                Demander une démo
              </Link>
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 px-6 pb-6">
        <p className="max-w-md mx-auto text-[11px] text-[#9CA3AF] text-center">
          © 2026 wyvio · Un espace collaboratif pour vos ventes automobiles.
        </p>
      </footer>
    </div>
  );
};

export default Login;
